import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { useRouterProps } from '../hooks/useRouterProps';
import { isFramed } from 'utils/lsgoAuthEmbedSession';
import { resolveSisSchool, sisSchoolIdFrom } from 'utils/sisSchoolAuth';
import LsgoAuthEmbedSignIn from 'containers/Auth/LsgoAuthEmbedSignIn';

/**
 * Inside the SIS iframe: lsgo-authentication sign-in for a migrated school,
 * else the legacy SSO redirect (also when the school can't be resolved).
 */
const SisEmbedGate = ({ ssoType, search, legacyPath }) => {
    const [school, setSchool] = React.useState(null);
    React.useEffect(() => {
        let live = true;
        resolveSisSchool(sisSchoolIdFrom(search)).then((resolved) => live && setSchool(resolved));
        return () => {
            live = false;
        };
    }, [search]);
    if (!school) return null;
    if (!school.useLsgoAuth) return <Navigate to={legacyPath} replace />;
    return <LsgoAuthEmbedSignIn ssoType={ssoType} tenant={school.tenant} />;
};

/**
 * SSO authentication route guard
 * Handles sentral and schoolBytes SSO authentication flows
 */
const SSORoute = ({
    children,
    render,
    ssoType = 'sentral', // 'sentral' | 'schoolBytes'
    redirectTo,
    ...props
}) => {
    const location = useLocation();
    const auth = useAuth();
    const routerProps = useRouterProps();

    // If authenticated, allow access
    if (auth.isAuthenticated) {
        return render ? render({ ...props, ...routerProps }) : children;
    }

    // Not authenticated, redirect to SSO login
    const authMethod = ssoType === 'schoolBytes' ? 'schoolBytes_sso' : 'sentral_sso';
    const loginPath = `/?redirectUri=${encodeURIComponent(
        location.pathname + location.search
    )}&auth_method=${authMethod}`;

    // Inside the SIS iframe, a school on lsgo-authentication signs in here
    // (stored session, silent, then a popup button); others keep legacy SSO.
    // Top level, the SSO link (loginPath) routes the same way in Login.js.
    if (isFramed()) {
        return <SisEmbedGate ssoType={ssoType} search={location.search} legacyPath={loginPath} />;
    }

    return <Navigate to={loginPath} replace />;
};

export default SSORoute;
